import React, { useState } from "react";
import CTAButton from "@/components/ui/CTAButton";
import useScrollReveal from "@/hooks/useScrollReveal";
import { COLORS, FONTS, LAYOUT } from "@/constants/theme";

const INITIAL_FORM = {
  fullName: "",
  organization: "",
  role: "",
  email: "",
  phone: "",
  message: "",
  source: "",
};

const SOURCE_OPTIONS = ["Google", "Referido", "LinkedIn", "Otro"];

const FIELD_BASE_STYLE = {
  backgroundColor: COLORS.lightSurface,
  borderRadius: "4px",
  color: COLORS.lightText,
  fontFamily: FONTS.body,
};

/**
 * Campo flotante reusable para inputs de texto.
 *
 * @param {{
 *   id: string,
 *   name: string,
 *   label: string,
 *   value: string,
 *   required?: boolean,
 *   type?: string,
 *   onChange: (event: React.ChangeEvent<HTMLInputElement>) => void,
 *   error?: string
 * }} props
 * @returns {JSX.Element}
 */
const FloatingInput = ({ id, name, label, value, required = false, type = "text", onChange, error }) => {
  const [isFocused, setIsFocused] = useState(false);
  const active = isFocused || value.length > 0;

  return (
    <div>
      <div className="relative">
        <input
          className="w-full border px-3 pb-2 pt-5 text-[1rem] transition-colors focus:outline-none"
          id={id}
          name={name}
          onBlur={() => setIsFocused(false)}
          onChange={onChange}
          onFocus={() => setIsFocused(true)}
          required={required}
          style={{
            ...FIELD_BASE_STYLE,
            borderColor: isFocused ? COLORS.borderStrong : COLORS.lightBorder,
          }}
          type={type}
          value={value}
        />
        <label
          htmlFor={id}
          style={{
            position: "absolute",
            left: "0.75rem",
            top: "1rem",
            transformOrigin: "left center",
            transition: "transform 180ms ease, color 180ms ease",
            transform: active ? "translateY(-0.68rem) scale(0.88)" : "translateY(0) scale(1)",
            color: active ? COLORS.digitalBlue : COLORS.lightMuted,
            fontFamily: FONTS.body,
            fontSize: "0.9rem",
          }}
        >
          {label}
          {required ? " *" : ""}
        </label>
      </div>
      {error ? (
        <p className="mt-1 text-sm" style={{ color: COLORS.webOrange, fontFamily: FONTS.body }}>
          {error}
        </p>
      ) : null}
    </div>
  );
};

/**
 * Campo flotante para textarea.
 *
 * @param {{
 *   id: string,
 *   name: string,
 *   label: string,
 *   value: string,
 *   required?: boolean,
 *   onChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => void,
 *   error?: string
 * }} props
 * @returns {JSX.Element}
 */
const FloatingTextarea = ({ id, name, label, value, required = false, onChange, error }) => {
  const [isFocused, setIsFocused] = useState(false);
  const active = isFocused || value.length > 0;

  return (
    <div>
      <div className="relative">
        <textarea
          className="w-full border px-3 pb-2 pt-5 text-[1rem] transition-colors focus:outline-none"
          id={id}
          name={name}
          onBlur={() => setIsFocused(false)}
          onChange={onChange}
          onFocus={() => setIsFocused(true)}
          required={required}
          rows={4}
          style={{
            ...FIELD_BASE_STYLE,
            borderColor: isFocused ? COLORS.borderStrong : COLORS.lightBorder,
            resize: "vertical",
          }}
          value={value}
        />
        <label
          htmlFor={id}
          style={{
            position: "absolute",
            left: "0.75rem",
            top: "1rem",
            transformOrigin: "left center",
            transition: "transform 180ms ease, color 180ms ease",
            transform: active ? "translateY(-0.68rem) scale(0.88)" : "translateY(0) scale(1)",
            color: active ? COLORS.digitalBlue : COLORS.lightMuted,
            fontFamily: FONTS.body,
            fontSize: "0.9rem",
          }}
        >
          {label}
          {required ? " *" : ""}
        </label>
      </div>
      {error ? (
        <p className="mt-1 text-sm" style={{ color: COLORS.webOrange, fontFamily: FONTS.body }}>
          {error}
        </p>
      ) : null}
    </div>
  );
};

/**
 * Campo flotante para select.
 *
 * @param {{
 *   id: string,
 *   name: string,
 *   label: string,
 *   value: string,
 *   onChange: (event: React.ChangeEvent<HTMLSelectElement>) => void
 * }} props
 * @returns {JSX.Element}
 */
const FloatingSelect = ({ id, name, label, value, onChange }) => {
  return (
    <div>
      <label
        htmlFor={id}
        style={{
          display: "block",
          marginBottom: "0.45rem",
          color: COLORS.lightMuted,
          fontFamily: FONTS.body,
          fontSize: "0.9rem",
        }}
      >
        {label}
      </label>
      <select
        className="w-full border px-3 py-3 text-[1rem] transition-colors focus:outline-none"
        id={id}
        name={name}
        onChange={onChange}
        style={{
          ...FIELD_BASE_STYLE,
          borderColor: COLORS.lightBorder,
          lineHeight: 1.4,
        }}
        value={value}
      >
        <option disabled value="">
          Selecciona una opción
        </option>
        {SOURCE_OPTIONS.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    </div>
  );
};

/**
 * Sección de contacto con formulario validado en frontend.
 *
 * Recibe los servicios que el visitante marcó más arriba y los muestra como
 * fichas quitables. No se vuelcan en el textarea a propósito: pisaría lo que
 * la persona esté escribiendo.
 *
 * @param {{
 *   serviciosElegidos?: string[],
 *   onQuitarServicio?: (titulo: string) => void
 * }} props
 * @returns {JSX.Element}
 */
const ContactSection = ({ serviciosElegidos = [], onQuitarServicio }) => {
  const [sectionRef, sectionVisible] = useScrollReveal(0.2);
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState("");

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((previous) => ({ ...previous, [name]: value }));
    setErrors((previous) => ({ ...previous, [name]: "" }));
  };

  const validate = () => {
    const nextErrors = {};

    if (!formData.fullName.trim()) {
      nextErrors.fullName = "Ingresa el nombre completo.";
    }
    if (!formData.organization.trim()) {
      nextErrors.organization = "Ingresa la organización o empresa.";
    }
    if (!formData.email.trim()) {
      nextErrors.email = "Ingresa el correo institucional.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      nextErrors.email = "Ingresa un correo electrónico válido.";
    }
    if (!formData.message.trim()) {
      nextErrors.message = "Describe brevemente lo que necesitas.";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!validate()) {
      setSuccessMessage("");
      return;
    }

    // El formulario todavía no envía a ningún sitio (§11.C1 de TRAZABILIDAD.md).
    // Se arma aquí la solicitud completa —con los servicios marcados— para que
    // conectarla a un backend sea solo cambiar esta línea.
    const solicitud = { ...formData, servicios: serviciosElegidos };
    void solicitud;

    setSuccessMessage("Gracias. Te contactaremos pronto.");
    setFormData(INITIAL_FORM);
    serviciosElegidos.forEach((servicio) => onQuitarServicio && onQuitarServicio(servicio));
  };

  return (
    <section
      className="px-6 py-24 md:py-32"
      id="contacto"
      style={{ backgroundColor: COLORS.lightSection, scrollMarginTop: LAYOUT.scrollOffset }}
    >
      {/* -- ContactSection -- */}
      <div
        className={`reveal ${sectionVisible ? "visible" : ""} mx-auto grid grid-cols-1 gap-10 md:grid-cols-2`}
        ref={sectionRef}
        style={{ maxWidth: LAYOUT.maxWidth }}
      >
        <div>
          <h2
            className="text-[2.1rem] md:text-[3rem]"
            style={{
              color: COLORS.lightText,
              fontFamily: FONTS.display,
              lineHeight: 1.12,
              fontWeight: 600,
              letterSpacing: "-0.015em",
            }}
          >
            Conversemos sobre la decisión que necesitas tomar.
          </h2>
          <p
            className="mt-6 text-[1.08rem] md:text-[1.14rem]"
            style={{
              color: COLORS.lightMuted,
              fontFamily: FONTS.body,
              lineHeight: 1.7,
            }}
          >
            Cuéntanos qué decisión tienes por delante y agendamos una conversación sin compromiso. Revisamos el
            contexto de tu organización y te decimos con qué análisis podemos respaldarla.
          </p>

          <ul className="mt-8 space-y-4">
            <li className="flex items-center gap-3">
              <svg fill="none" height="18" viewBox="0 0 24 24" width="18">
                <path
                  d="M4 7h16v10H4z"
                  stroke={COLORS.digitalBlue}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="1.8"
                />
                <path d="m4 8 8 6 8-6" stroke={COLORS.digitalBlue} strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" />
              </svg>
              <span style={{ color: COLORS.lightText, fontFamily: FONTS.body, fontSize: "1rem" }}>
                contacto@altamaec.com
              </span>
            </li>
            <li className="flex items-center gap-3">
              <svg fill="none" height="18" viewBox="0 0 24 24" width="18">
                <path
                  d="M12 21s7-6.2 7-11a7 7 0 1 0-14 0c0 4.8 7 11 7 11Z"
                  stroke={COLORS.digitalBlue}
                  strokeWidth="1.8"
                />
                <circle cx="12" cy="10" r="2.2" stroke={COLORS.digitalBlue} strokeWidth="1.8" />
              </svg>
              <span style={{ color: COLORS.lightText, fontFamily: FONTS.body, fontSize: "1rem" }}>Guayaquil, Ecuador.</span>
            </li>
            <li className="flex items-center gap-3">
              <svg fill="none" height="18" viewBox="0 0 24 24" width="18">
                <circle cx="12" cy="12" r="8" stroke={COLORS.digitalBlue} strokeWidth="1.8" />
                <path d="M12 8v4l3 2" stroke={COLORS.digitalBlue} strokeLinecap="round" strokeWidth="1.8" />
              </svg>
              <span style={{ color: COLORS.lightText, fontFamily: FONTS.body, fontSize: "1rem" }}>
                Lunes a viernes, 9:00 - 18:00.
              </span>
            </li>
          </ul>
        </div>

        <form
          className="space-y-4 rounded-lg border p-6"
          noValidate
          onSubmit={handleSubmit}
          style={{
            backgroundColor: COLORS.lightSurface,
            borderColor: COLORS.lightBorder,
          }}
        >
          <FloatingInput
            error={errors.fullName}
            id="fullName"
            label="Nombre completo"
            name="fullName"
            onChange={handleChange}
            required
            value={formData.fullName}
          />

          <FloatingInput
            error={errors.organization}
            id="organization"
            label="Organización / Empresa"
            name="organization"
            onChange={handleChange}
            required
            value={formData.organization}
          />

          <FloatingInput id="role" label="Cargo" name="role" onChange={handleChange} value={formData.role} />

          <FloatingInput
            error={errors.email}
            id="email"
            label="Correo institucional"
            name="email"
            onChange={handleChange}
            required
            type="email"
            value={formData.email}
          />

          <FloatingInput
            id="phone"
            label="Teléfono (opcional)"
            name="phone"
            onChange={handleChange}
            type="tel"
            value={formData.phone}
          />

          {serviciosElegidos.length > 0 ? (
            <div
              className="rounded-md border p-3"
              style={{ borderColor: COLORS.lightBorder, backgroundColor: "rgba(0,104,255,0.04)" }}
            >
              <p
                className="mb-2.5 text-[0.76rem] uppercase"
                style={{ color: COLORS.lightMuted, fontFamily: FONTS.data, letterSpacing: "0.1em" }}
              >
                Servicios que te interesan
              </p>
              <ul className="flex flex-wrap gap-2" style={{ listStyle: "none", margin: 0, padding: 0 }}>
                {serviciosElegidos.map((servicio) => (
                  <li key={servicio}>
                    <button
                      className="ficha-servicio inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-[0.82rem]"
                      onClick={() => onQuitarServicio && onQuitarServicio(servicio)}
                      style={{
                        backgroundColor: "rgba(0,104,255,0.1)",
                        border: `1px solid ${COLORS.digitalBlue}`,
                        color: COLORS.digitalBlue,
                        fontFamily: FONTS.body,
                        cursor: "pointer",
                        transition: "background-color 180ms ease",
                      }}
                      type="button"
                    >
                      {servicio}
                      <svg aria-hidden="true" fill="none" height="10" viewBox="0 0 10 10" width="10">
                        <path
                          d="M1.5 1.5l7 7M8.5 1.5l-7 7"
                          stroke="currentColor"
                          strokeLinecap="round"
                          strokeWidth="1.6"
                        />
                      </svg>
                      <span className="sr-only">Quitar</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          <FloatingTextarea
            error={errors.message}
            id="message"
            label="Mensaje / ¿Qué necesitas?"
            name="message"
            onChange={handleChange}
            required
            value={formData.message}
          />

          <FloatingSelect
            id="source"
            label="¿Cómo nos encontraste?"
            name="source"
            onChange={handleChange}
            value={formData.source}
          />

          <CTAButton className="mt-2" fullWidth type="submit">
            Enviar solicitud
          </CTAButton>

          {successMessage ? (
            <p className="form-success-enter text-base" style={{ color: COLORS.digitalBlue, fontFamily: FONTS.body }}>
              {successMessage}
            </p>
          ) : null}
        </form>
      </div>
    </section>
  );
};

export default ContactSection;
